import { useCallback, useEffect, useRef } from 'react';
import { useAccount, useSignMessage as useSignMessage$1, useBlock, useWatchBlocks, useBlockNumber as useBlockNumber$1, useReadContracts as useReadContracts$1, useReadContract as useReadContract$1, useBalance as useBalance$1, usePublicClient, useConfig } from 'wagmi';
export { WagmiProvider, useAccount, useAccountEffect, useChainId, useConfig, useConnect, useDisconnect, useEstimateFeesPerGas, useEstimateGas, usePublicClient, useSendTransaction, useSwitchChain, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { watchAccount } from '@wagmi/core';

// src/hooks/useSignMessage.ts
function useSignMessage() {
  const { address, connector } = useAccount();
  const { signMessageAsync: sign } = useSignMessage$1();
  return {
    signMessageAsync: useCallback(
      async (args) => {
        if (connector?.id === "bsc" && window.BinanceChain && address) {
          const res = await window.BinanceChain.bnbSign?.(address, args.message);
          if (res) {
            return res.signature;
          }
          return null;
        }
        return sign(args);
      },
      [address, connector?.id, sign]
    )
  };
}
function createKeyGetter(name) {
  return function getKey(chainId) {
    return [name, chainId];
  };
}
var getBlockNumberQueryKey = createKeyGetter("blockNumber");
var getBlockTimestampQueryKey = createKeyGetter("blockTimestamp");
var getInitialBlockNumberQueryKey = createKeyGetter("initialBlockNumber");
var getInitialBlockTimestampQueryKey = createKeyGetter("initialBlockTimestamp");
function useWatchBlock({ chainId, enabled }) {
  const queryClient = useQueryClient();
  const queryEnabled = Boolean(chainId && enabled);
  const { data: initialBlock } = useBlock({
    chainId,
    blockTag: "latest",
    query: {
      enabled: queryEnabled
    }
  });
  useEffect(() => {
    if (!initialBlock) {
      return;
    }
    const { number: blockNumber, timestamp: blockTimestamp } = initialBlock;
    queryClient.setQueryData(getBlockNumberQueryKey(chainId), blockNumber);
    const initialBlockNumberQueryKey = getInitialBlockNumberQueryKey(chainId);
    if (!queryClient.getQueryCache().find({
      queryKey: initialBlockNumberQueryKey
    })?.state?.data) {
      queryClient.setQueryData(initialBlockNumberQueryKey, blockNumber);
    }
    const initialBlockTimestampQueryKey = getInitialBlockTimestampQueryKey(chainId);
    if (!queryClient.getQueryCache().find({
      queryKey: initialBlockTimestampQueryKey
    })?.state?.data) {
      queryClient.setQueryData(initialBlockTimestampQueryKey, Number(blockTimestamp));
    }
  }, [chainId, initialBlock, queryClient]);
  useWatchBlocks({
    chainId,
    blockTag: "latest",
    enabled: queryEnabled,
    onBlock: (data) => {
      const blockNumber = Number(data.number);
      const timestamp = Number(data.timestamp);
      queryClient.setQueryData(getBlockNumberQueryKey(chainId), blockNumber);
      queryClient.setQueryData(getBlockTimestampQueryKey(chainId), timestamp);
    }
  });
}
function useBlockNumber({
  chainId,
  watch
}) {
  const watchedBlockNumber = useWatchedBlockNumber({ chainId });
  const blockNumber = useBlockNumber$1({ chainId, query: { enabled: !watch }, watch: false });
  return watch ? watchedBlockNumber : blockNumber;
}
function useWatchedBlockNumber({ chainId }) {
  return useQuery({
    queryKey: getBlockNumberQueryKey(chainId),
    enabled: false,
    queryFn: async () => {
      throw new Error("queryFn not implemented \u2013 data is set manually via setQueryData");
    },
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
}
function useBlockTimestamp({ chainId }) {
  return useQuery({
    queryKey: getBlockTimestampQueryKey(chainId),
    enabled: false,
    queryFn: async () => {
      throw new Error("queryFn not implemented \u2013 data is set manually via setQueryData");
    },
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
}
function useInitialBlockNumber({ chainId }) {
  return useQuery({
    queryKey: getInitialBlockNumberQueryKey(chainId),
    enabled: false,
    queryFn: async () => {
      throw new Error("queryFn not implemented \u2013 data is set manually via setQueryData");
    },
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
}
function useInitialBlockTimestamp({ chainId }) {
  return useQuery({
    queryKey: getInitialBlockTimestampQueryKey(chainId),
    enabled: false,
    queryFn: async () => {
      throw new Error("queryFn not implemented \u2013 data is set manually via setQueryData");
    },
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
}
var useDidMountEffect = (func, checkObject) => {
  const isFirstRun = useRef(true);
  const prevParams = useRef(checkObject);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (prevParams.current !== void 0 && checkObject !== prevParams.current) {
      func();
    }
    prevParams.current = checkObject;
  }, [checkObject]);
};
var useDidMountEffect_default = useDidMountEffect;

// src/hooks/useReadContracts.ts
function useReadContracts(parameters = {}) {
  const { watch, ...queryParameters } = parameters;
  const queryClient = useQueryClient();
  const { data: blockNumber } = useBlockNumber({ watch, chainId: parameters.contracts?.[0]?.chainId });
  const readContractResult = useReadContracts$1(queryParameters);
  useDidMountEffect_default(() => {
    if (watch) {
      queryClient.invalidateQueries({ queryKey: readContractResult.queryKey }, { cancelRefetch: false });
    }
  }, blockNumber);
  return readContractResult;
}
function useReadContract(parameters = {}) {
  const { watch, ...queryParameters } = parameters;
  const queryClient = useQueryClient();
  const { data: blockNumber } = useBlockNumber({ chainId: parameters.chainId, watch });
  const readContractResult = useReadContract$1(queryParameters);
  useDidMountEffect_default(() => {
    if (watch) {
      queryClient.invalidateQueries({ queryKey: readContractResult.queryKey }, { cancelRefetch: false });
    }
  }, blockNumber);
  return readContractResult;
}
function useBalance(params = {}) {
  const { watch, ...queryParameters } = params;
  const queryClient = useQueryClient();
  const { data: blockNumber } = useBlockNumber({ chainId: params.chainId, watch });
  const readContractResult = useBalance$1(queryParameters);
  useDidMountEffect_default(() => {
    if (watch) {
      queryClient.invalidateQueries({ queryKey: readContractResult.queryKey }, { cancelRefetch: false });
    }
  }, blockNumber);
  return readContractResult;
}
function useFeeData(parameters = {}) {
  const { watch, ...queryParameters } = parameters;
  const queryClient = useQueryClient();
  const { data: blockNumber } = useBlockNumber({ watch, chainId: parameters.chainId });
  const readContractResult = useBalance$1(queryParameters);
  useDidMountEffect_default(() => {
    if (watch) {
      queryClient.invalidateQueries({ queryKey: readContractResult.queryKey }, { cancelRefetch: false });
    }
  }, blockNumber);
  return readContractResult;
}
function useContractEvents(args) {
  const publicClient = usePublicClient();
  if (args.blockHash === void 0) {
    if (args.fromBlock === void 0) args.fromBlock = "earliest";
    if (args.toBlock === void 0) args.toBlock = "latest";
  }
  const queryFn = async () => {
    if (publicClient === void 0) {
      throw new Error(`Tried to query contract events when publicClient was undefined.`);
    }
    return publicClient.getContractEvents(args);
  };
  const queryKey = ["useContractEvents", publicClient?.chain.id, args];
  return useQuery({
    queryKey,
    queryFn,
    staleTime: Infinity,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: [],
    enabled: args.query?.enabled
  });
}
var useAccountChange = (onChange) => {
  const { connector: activeConnector } = useAccount();
  const config = useConfig();
  useEffect(() => {
    const unwatch = watchAccount(config, { onChange });
    return () => unwatch();
  }, [activeConnector]);
};

export { getBlockNumberQueryKey, getBlockTimestampQueryKey, getInitialBlockNumberQueryKey, getInitialBlockTimestampQueryKey, useAccountChange, useBalance, useBlockNumber, useBlockTimestamp, useContractEvents, useFeeData, useInitialBlockNumber, useInitialBlockTimestamp, useReadContract, useReadContracts, useSignMessage, useWatchBlock, useWatchedBlockNumber };
